import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "@fluentui/react-components";
import styled from "styled-components";
import { SectionWrapper } from "./SharedSectionStyles";
import CompactField from "components/CompactField/CompactField";
import { useVehiclePrefillMutation } from "state/queries/useVehiclePrefill";
import { mapVehicleInfoToFormValues } from "utils/proposalTransformers";
import { usePrefillContext } from "pages/Proposal/contexts/PrefillContext";
import { ClientSearchResult } from "state/queries/useClientSearch";
import { populateClientDataFromGOS } from "./clientDataHelpers";
import { ValidationErrorDisplay } from "components/ValidationErrorDisplay";
import api from "service/api";
import { useTaxonomy } from "library/utils/useTaxonomy";
// import { useTaxonomyFieldConverter } from "library/utils/useTaxonomyValue";

const PrefillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) auto auto;
  gap: 16px;
  align-items: end;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  min-height: 48px; /* Match the height of input fields with label (12px label + 4px gap + 32px input) */
  padding-bottom: 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  transform: translateY(3px); /* Move button up to align with input content */
`;

interface PrefillSectionProps {
  getError?: (fieldPath: string) => string | undefined;
  offerId?: string | null;
}

const PrefillSection: React.FC<PrefillSectionProps> = ({
  getError,
  offerId,
}) => {
  const { t } = useTranslation();
  const methods = useFormContext();
  const { isPrefilling, setIsPrefilling } = usePrefillContext();
  const taxonomy = useTaxonomy();

  // Auto-convert IDs to codes for taxonomy fields (commented out - field removed from form)
  // useTaxonomyFieldConverter("proposalData.offerStatus", "OfferStatus");

  // Create options for Offer Status (commented out - field removed from form)
  // const offerStatusOptions = useMemo(() => {
  //   if (!taxonomy.OfferStatus?.items) {
  //     return [];
  //   }
  //   return taxonomy.OfferStatus.items.map((item) => ({
  //     key: item.code,
  //     text: t(`${item.code}`, item.code),
  //   }));
  // }, [taxonomy, t]);

  // Prefill mutation with callback
  const { mutate: prefillVehicleInfo } = useVehiclePrefillMutation(
    async (vehicleInfo) => {
      // Only process if the prefill was successful
      if (!vehicleInfo.Success) {
        // Clear prefilling state and exit early
        setIsPrefilling(false);
        return;
      }

      // Use the mapping utility function to get all form field values
      const isMtplPolicy =
        methods.getValues("proposalData.insuranceProduct") === "MTPL";
      const mappedValues = mapVehicleInfoToFormValues(
        vehicleInfo,
        t,
        taxonomy,
        isMtplPolicy
      );

      // Apply the mapped values to the form
      Object.entries(mappedValues).forEach(([field, value]) => {
        methods.setValue(field as any, value);
      });

      // Check if we got client code from the prefill and try to find matching GOS client
      if (vehicleInfo.Client?.Code) {
        try {
          const clientCode = vehicleInfo.Client.Code;
          const isCompany =
            vehicleInfo.Client.IsCompany === true ||
            vehicleInfo.Client.Company === true;

          // Search for the GOS client by exact code (company register number
          // for legal entities, personal identification number for persons)
          const response = await api.get(
            `/clientssearch/${isCompany}/${encodeURIComponent(clientCode)}`
          );

          const matchingClient: ClientSearchResult | null =
            response.data || null;

          if (matchingClient) {
            // Use the same logic as when client is selected from search dialog
            populateClientDataFromGOS(matchingClient, methods.setValue);

            toast.info(
              t(
                "qtg.proposal.gosClientFound",
                "Matching GOS client found and populated"
              )
            );
          }
        } catch (error) {
          // Silently fail GOS client search - prefill data is already populated
          console.warn("Failed to search for GOS client:", error);
        }
      }

      // Trigger form validation after setting values
      methods.trigger();

      // Clear prefilling state
      setIsPrefilling(false);
    },
    // onFinally callback to clear spinner when operation is not successful or errors
    () => {
      setIsPrefilling(false);
    }
  );

  // Prefill handler
  const handlePrefill = useCallback(() => {
    const formData = methods.getValues();
    const prefillData = {
      licensePlate: formData.vehicleData.licensePlateNumber,
      vin: formData.vehicleData.vin,
      registrationCertificateNumber:
        formData.vehicleData.technicalPassportNumber,
      ownerCode: formData.vehicleData.ownerCode,
    };

    // Check if at least one field is filled
    if (
      !prefillData.licensePlate &&
      !prefillData.vin &&
      !prefillData.registrationCertificateNumber &&
      !prefillData.ownerCode
    ) {
      toast.error(t("qtg.proposal.prefillRequiredFields"));
      return;
    }

    // Set prefilling state to true
    setIsPrefilling(true);

    prefillVehicleInfo(prefillData);
  }, [methods, prefillVehicleInfo, setIsPrefilling, t]);

  return (
    <SectionWrapper>
      {/* <SectionTitle>
        {t("qtg.proposal.generalInfo.prefillInfo", "General Information")}
      </SectionTitle> */}

      <PrefillGrid>
        {/* Offer Status field - commented out, now displayed in header */}
        {/* <CompactField
          type="dropdown"
          name="proposalData.offerStatus"
          label="Offer Status"
          translationKey="qtg.proposal.proposalData.offerStatus"
          placeholder="Select Offer Status"
          options={offerStatusOptions}
        /> */}
        <div>
          <CompactField
            type="input"
            name="vehicleData.licensePlateNumber"
            dataHelp="offer-general-plate"
            label="License Plate Number"
            translationKey="qtg.proposal.vehicleData.licensePlateNumber"
            placeholder=""
            required={true}
            readOnly={!!offerId}
          />
          {getError && (
            <ValidationErrorDisplay error={getError("Vehicle.LicensePlate")} />
          )}
        </div>
        <div>
          <CompactField
            type="input"
            name="vehicleData.vin"
            dataHelp="offer-general-vin"
            label="VIN"
            translationKey="qtg.proposal.vehicleData.vin"
            placeholder=""
            required={true}
            readOnly={!!offerId}
          />
          {getError && (
            <ValidationErrorDisplay error={getError("Vehicle.Vin")} />
          )}
        </div>
        <div>
          <CompactField
            type="input"
            name="vehicleData.technicalPassportNumber"
            dataHelp="offer-general-certificate"
            label="Vehicle Certificate Number"
            translationKey="qtg.proposal.vehicleData.technicalPassportNumber"
            placeholder=""
            required={true}
            readOnly={!!offerId}
          />
          {getError && (
            <ValidationErrorDisplay
              error={getError("Vehicle.RegistrationCertificateNumber")}
            />
          )}
        </div>{" "}
        <div>
          <CompactField
            type="input"
            name="vehicleData.ownerCode"
            dataHelp="offer-general-owner-code"
            label="Owner Code"
            translationKey="qtg.proposal.vehicleData.ownerCode"
            placeholder=""
            required={true}
            readOnly={!!offerId}
          />{" "}
          {getError && (
            <ValidationErrorDisplay error={getError("Vehicle.Owner.Code")} />
          )}
        </div>
        <CheckboxWrapper>
          <CompactField
            type="checkbox"
            name="vehicleData.isLeased"
            dataHelp="offer-general-leased"
            label="Vehicle is Leased"
            translationKey="qtg.proposal.vehicleData.isLeased"
            readOnly={!!offerId}
          />
        </CheckboxWrapper>
        <ButtonWrapper>
          <Button
            data-help="offer-general-prefill-button"
            appearance="primary"
            onClick={handlePrefill}
            disabled={isPrefilling || !!offerId}
          >
            {isPrefilling
              ? t("qtg.proposal.buttons.prefillLoading")
              : t("qtg.proposal.buttons.prefill")}
          </Button>
        </ButtonWrapper>
      </PrefillGrid>
    </SectionWrapper>
  );
};

export default PrefillSection;
